import { useContext } from 'react';
import api from '../api/api';
import { Quota } from '../models/common-types';
import { useMutation, useQuery } from 'react-query';
import { AuthContext } from './authContext';

export default function useAdmin(wsId: number | null) {
    const {
        user: { role },
    } = useContext(AuthContext);

    const { data: quota } = useQuery<Quota>(['quota'], () => api.quota.fetchQuota(), {
        enabled: !!role && role !== 'Regular',
    });

    const { isError } = useQuery<Quota>(
        ['quotaValid', wsId],
        ({ queryKey }) => api.quota.fetchQuotaValid(queryKey[1] as number),
        {
            enabled: !!wsId,
            retry: false,
        },
    );

    const uploadPreferencesImage = useMutation(
        (imageData: {
            fileName: string;
            data: Uint8Array;
            successCallback: (result) => void;
            errorCallback: () => void;
        }) => api.report.uploadPreferencesImage(imageData.data, imageData.fileName),
        {
            onSuccess: (data, imageData) => {
                imageData.successCallback(data);
            },
            onError: (data, imageData) => {
                console.log(data);
                imageData.errorCallback();
            },
        },
    );

    const uploadWatchList = useMutation(
        (watchListData: {
            fileName: string;
            data: Uint8Array;
            successCallback?: (result) => void;
            errorCallback?: (message) => void;
        }) => api.report.uploadWatchList(watchListData.data, watchListData.fileName),
        {
            onSuccess: (data, watchListData) => {
                watchListData.successCallback?.(data);
            },
            onError: (data: any, watchListData) => {
                console.log(data);
                watchListData.errorCallback?.(data.message);
            },
        },
    );

    const deleteWatchList = useMutation(
        (watchListData: { fileName: string; successCallback?: (result) => void; errorCallback?: () => void }) =>
            api.report.deleteWatchList(watchListData.fileName),
        {
            onSuccess: (data, watchListData) => {
                watchListData.successCallback?.(data);
            },
            onError: (data, watchListData) => {
                console.log(data);
                watchListData.errorCallback?.();
            },
        },
    );

    const getWatchList = useMutation(
        (watchListData: { id: string; successCallback?: (result) => void; errorCallback?: () => void }) =>
            api.report.getWatchList(watchListData.id),
        {
            onSuccess: (data, watchListData) => {
                watchListData.successCallback?.(data);
            },
            onError: (data, watchListData) => {
                console.log(data);
                watchListData.errorCallback?.();
            },
        },
    );

    const getFileFromUrl = useMutation(
        (fileData: { url: string; successCallback?: (result) => void; errorCallback?: () => void }) =>
            api.report.getFileFromUrl(fileData.url),
        {
            onSuccess: (data, fileData) => {
                fileData.successCallback?.(data);
            },
            onError: (data, fileData) => {
                console.log(data);
                fileData.errorCallback?.();
            },
        },
    );

    const updatePreferencesName = useMutation(
        (nameData: { name: string; successCallback: (result) => void; errorCallback: () => void }) =>
            api.report.updatePreferencesName(nameData.name),
        {
            onSuccess: (data, nameData) => {
                nameData.successCallback(data);
            },
            onError: (data, nameData) => {
                nameData.errorCallback();
            },
        },
    );

    const removePreferencesImage = useMutation(
        (argumentsData: { successCallback: (result) => void; errorCallback: () => void }) =>
            api.report.removePreferencesImage(),
        {
            onSuccess: (data, argumentsData) => {
                argumentsData.successCallback(data);
            },
            onError: (data, argumentsData) => {
                argumentsData.errorCallback();
            },
        },
    );

    return {
        quota,
        isQuotaLimitReached: isError,
        uploadPreferencesImage: uploadPreferencesImage.mutate,
        uploadWatchList: uploadWatchList.mutate,
        deleteWatchList: deleteWatchList.mutate,
        getWatchList: getWatchList.mutate,
        getFileFromUrl: getFileFromUrl.mutate,
        updatePreferencesName: updatePreferencesName.mutate,
        removePreferencesImage: removePreferencesImage.mutate,
    };
}
